'use strict';

/**
 * T576 P2 (LD-1b) — CLIENT-SIDE session re-unlock on reload.
 *
 * On a page reload the in-memory vault session (privateKeys.kex) is legitimately
 * gone (T213 allowlist never persists key material). This re-materializes it
 * WITHOUT any server key custody, reusing the EXISTING login/auth-proof path
 * (the T510 "unlock-on-host" precedent — the rejected /session-handoff/refresh
 * server-rewrap endpoint was NOT built):
 *
 *   POST /auth/vault/login/challenge  → { saltPw }               (public salt)
 *   deriveAuthProof(password, saltPw)                            (client Argon2id)
 *   POST /auth/vault/login            → { identityVaultMeta/Bin } (CIPHERTEXT)
 *   unlockIdentityVault(...)          → privateKeys              (CLIENT unwrap)
 *
 * The server returns only the WRAPPED vault (ciphertext) — it never holds,
 * derives, or returns the unwrapped kex (Legal Foundation §1/§2). Fail-closed:
 * a wrong password or a failed challenge throws and installs NO session.
 *
 * Pure + injectable (fetchImpl / vault / events) so it is Node-testable and
 * browser-reusable. The browser wiring passes window.fetch + window.OysterVault
 * + a document-dispatching events shim.
 *
 * ── T451 P3.2b — SESSION-BOUND mode (no `email`) ─────────────────────────────
 * The host re-unlock surface (shared/crypto/vault-reunlock-surface.js) calls
 * this with `{ password }` only. The path above RE-LOGS-IN through
 * POST /auth/vault/login, which MINTS A NEW SESSION COOKIE — and the server's
 * warm master-key map is keyed by the opaque session token
 * (master-key-session.js :: setMasterKey(sessionToken, …)), so a re-login from
 * a live host tab would orphan the warm key and 412 every still-server-side
 * write until P3.4 retires them; it would also trip MFA step-up on an MFA
 * account and burn a login-throttle slot. Session-bound mode instead reads
 *
 *   GET /auth/vault/me                → { identityVaultMeta/Bin }  (CIPHERTEXT, same cookie)
 *   unlockIdentityVault(...)          → privateKeys + _identityMaster (CLIENT unwrap)
 *
 * — the same wrapped vault the login lane returns, under the EXISTING session.
 * No identifier, no re-login, no new cookie, no key on the wire. The email
 * mode is unchanged byte-for-byte for the reload precedent that needs it.
 */

/**
 * @param {object} args
 * @param {string} [args.email]  login identifier — OMIT for session-bound mode (T451 P3.2b)
 * @param {string} args.password
 * @param {string} [args.basePrefix]  app base prefix ('' by default)
 * @param {Function} [args.fetchImpl]  fetch (defaults to global fetch)
 * @param {object} [args.vault]        OysterVault (deriveAuthProof + unlockIdentityVault + session)
 * @param {object} [args.kdfProfile]   Argon2id profile (defaults to DESKTOP inside unlockIdentityVault)
 * @param {{dispatch:Function}} [args.events]  vault-ready dispatcher (defaults to document event)
 * @returns {Promise<boolean>}
 */
async function reunlockSession(args) {
  const { email, password, basePrefix, fetchImpl, vault, kdfProfile } = args || {};
  const f = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  const v = vault || (typeof window !== 'undefined' ? window.OysterVault : null);
  const base = basePrefix || '';
  if (!f) throw new Error('reunlockSession: no fetch available');
  if (!v || typeof v.deriveAuthProof !== 'function' || typeof v.unlockIdentityVault !== 'function') {
    throw new Error('reunlockSession: vault (deriveAuthProof + unlockIdentityVault) required');
  }
  if (typeof password !== 'string' || !password) throw new Error('reunlockSession: password required');
  const sessionBound = !(typeof email === 'string' && email);

  const events = args && args.events
    ? args.events
    : { dispatch(ev) { try { document.dispatchEvent(new CustomEvent(ev)); } catch (_e) {} } };

  const json = { 'content-type': 'application/json' };

  let lg;
  if (sessionBound) {
    // T451 P3.2b — 1–3 collapse to ONE session-bound read of the WRAPPED vault
    // (ciphertext; requireVaultAuth on the existing cookie; 404 for a legacy
    // account with no vault). Nothing is minted, nothing is sent but the cookie.
    const meRes = await f(base + '/auth/vault/me', { method: 'GET', credentials: 'same-origin' });
    lg = await meRes.json().catch(() => null);
    if (!meRes.ok || !lg || !lg.ok || !lg.identityVaultMeta || !lg.identityVaultBin) {
      throw new Error('re-unlock vault fetch failed');
    }
  } else {
    // 1. Challenge — public per-account saltPw only (anti-enumeration). No keys.
    const chRes = await f(base + '/auth/vault/login/challenge', {
      method: 'POST', credentials: 'same-origin', headers: json,
      body: JSON.stringify({ email }),
    });
    const ch = await chRes.json();
    if (!chRes.ok || !ch || !ch.ok || !ch.saltPw) throw new Error('re-unlock challenge failed');

    // 2. Client-side Argon2id auth proof — the raw password never leaves the tab.
    const passwordProof = v.deriveAuthProof({ password, saltPw: ch.saltPw });

    // 3. Authenticate → the server returns the WRAPPED identity vault (ciphertext).
    const lgRes = await f(base + '/auth/vault/login', {
      method: 'POST', credentials: 'same-origin', headers: json,
      body: JSON.stringify({ email, passwordProof }),
    });
    lg = await lgRes.json();
    if (!lgRes.ok || !lg || !lg.ok || !lg.identityVaultMeta || !lg.identityVaultBin) {
      throw new Error('re-unlock login failed');
    }
  }

  // 4. CLIENT-SIDE unwrap — the kex is re-derived HERE, in the browser. Throws
  //    on a wrong password (identityMaster unwrap fails) → we install nothing.
  const unlocked = v.unlockIdentityVault({
    password, meta: lg.identityVaultMeta, vaultBin: lg.identityVaultBin, kdfProfile,
  });

  // 5. Re-materialize the in-memory session (privateKeys are page-memory ONLY —
  //    NOT persisted, T213). Mirror login.html exactly.
  v.session = {
    accountNumber: lg.accountNumber,
    publicKeys: unlocked.publicKeys,
    privateKeys: unlocked.privateKeys,
    moduleMasters: unlocked.moduleMasters,
    policies: unlocked.policies,
  };
  if (unlocked._identityMaster) v.session._identityMaster = unlocked._identityMaster;

  // 5b. B2/B4 — the sign-only JIT session signer, derived at the ONE moment
  //     the master key is legitimately present (non-extractable WebCrypto
  //     where available — shared/crypto/jit-signer.js). Fail-soft: re-unlock
  //     must succeed even where the signer cannot be built; jitFetch then
  //     stays a pass-through.
  if (unlocked._identityMaster && typeof v.createJitSigner === 'function') {
    try {
      v.session.jitSigner = await v.createJitSigner({ masterKey: unlocked._identityMaster });
    } catch (_e) { /* no signer — gated reads degrade to today's refusal path */ }
  }

  // 6. Signal rehydration — the single vault-ready signal all consumers key off.
  try { if (typeof window !== 'undefined') window.__OYSTER_VAULT_READY__ = true; } catch (_e) {}
  events.dispatch('vault-ready');
  return true;
}

module.exports = { reunlockSession };
